import { useEffect, useMemo, useRef, useState } from "react";
import { getProperties } from "../api";

// One request per page while searching. The endpoint's own default is 20; asking
// for more only reduces the number of round trips, and the loop advances on what
// actually comes back rather than on what was asked for.
const PAGE_SIZE = 100;
// Backstop so a bad id can never walk the whole catalogue. 20 pages at the size
// above is far past any real portfolio; a short page ends the loop first in
// practice.
const MAX_PAGES = 20;

export const DEEP_LINK_PROPERTY_STATUS = Object.freeze({
  IDLE: "idle",
  RESOLVING: "resolving",
  FOUND: "found",
  NOT_FOUND: "not_found",
});

const sameId = (a, b) => String(a) === String(b);

/**
 * Pages through one property search for the id. Resolves the match, null once
 * the search runs out, or undefined when `isStale()` says nobody wants it.
 */
const findInSearch = async (searchText, propertyId, isStale) => {
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response = await getProperties({
      searchText,
      page,
      limit: PAGE_SIZE,
    });
    if (isStale()) return undefined;

    // Same shape PropertyStoreContext reads: data.data.properties.
    const batch = response?.data?.data?.properties ?? [];
    const match = batch.find((property) =>
      sameId(property?.propertyId, propertyId)
    );
    if (match) return match;
    // A short page is the last page.
    if (batch.length < PAGE_SIZE) return null;
  }
  return null;
};

/**
 * Finds the property a deep link names, within the user's own property list.
 *
 * The list is the access boundary: it is what the API returns for this user, so
 * a link forwarded to someone who does not hold that hotel resolves to
 * NOT_FOUND rather than switching them onto it. Resolving the id another way —
 * property metadata, say — would select a hotel that was never in the list and
 * leave access resting entirely on each later call being rejected.
 *
 * A match on the already-loaded page is derived during render, so the common
 * case (a hotel the user has open, or one of their first 20) costs no request
 * and no state.
 *
 * @param {string|number|null} propertyId from the URL
 * @param {Object[]} loadedProperties the context's current page
 * @param {number} totalCount how many the user has in total
 * @param {string} [searchText] hotel name, when the caller knows it. Narrows the
 *   paged search server-side — internal users hold far more properties than
 *   MAX_PAGES covers. The id match still decides; the name only filters.
 * @returns {{ property: Object|null, status: string }}
 */
const useDeepLinkedProperty = (
  propertyId,
  loadedProperties,
  totalCount,
  searchText = ""
) => {
  const localMatch = useMemo(() => {
    if (!propertyId) return null;
    return (
      (loadedProperties ?? []).find((property) =>
        sameId(property?.propertyId, propertyId)
      ) ?? null
    );
  }, [propertyId, loadedProperties]);

  // Whether paging could even help: if the loaded page already holds everything
  // the user has, an absent id is absent, full stop.
  const listIsComplete = (loadedProperties?.length ?? 0) >= (totalCount ?? 0);

  const [remote, setRemote] = useState({
    property: null,
    status: DEEP_LINK_PROPERTY_STATUS.IDLE,
  });
  // The id the remote state describes, so a search that finishes after the user
  // has navigated on cannot switch their property out from under them.
  const requestedRef = useRef(null);

  // ⚠ Every dependency here is a primitive. Callers pass `properties` straight
  // from context, and a parent re-render can hand over an equivalent-but-new
  // array; depending on that array (or on the matched object) would re-run this
  // effect, set state, and re-render — forever.
  const localMatchId = localMatch?.propertyId ?? null;

  useEffect(() => {
    if (!propertyId || localMatchId != null) {
      requestedRef.current = null;
      return;
    }

    requestedRef.current = propertyId;

    if (listIsComplete) {
      setRemote({
        property: null,
        status: DEEP_LINK_PROPERTY_STATUS.NOT_FOUND,
      });
      return;
    }

    setRemote({ property: null, status: DEEP_LINK_PROPERTY_STATUS.RESOLVING });

    let cancelled = false;

    const search = async () => {
      try {
        const isStale = () => cancelled || requestedRef.current !== propertyId;
        // The name hint first; unfiltered paging if it finds nothing, since the
        // hint may not match the property's own name exactly.
        const passes = searchText ? [searchText, ""] : [""];
        for (const text of passes) {
          const match = await findInSearch(text, propertyId, isStale);
          if (match === undefined) return;
          if (match) {
            setRemote({
              property: match,
              status: DEEP_LINK_PROPERTY_STATUS.FOUND,
            });
            return;
          }
        }
        setRemote({
          property: null,
          status: DEEP_LINK_PROPERTY_STATUS.NOT_FOUND,
        });
      } catch {
        // A failed search is indistinguishable from no access from here, and
        // guessing in the permissive direction is the wrong way to be wrong.
        if (cancelled || requestedRef.current !== propertyId) return;
        setRemote({
          property: null,
          status: DEEP_LINK_PROPERTY_STATUS.NOT_FOUND,
        });
      }
    };

    search();

    return () => {
      cancelled = true;
    };
  }, [propertyId, localMatchId, listIsComplete, searchText]);

  if (!propertyId) {
    return { property: null, status: DEEP_LINK_PROPERTY_STATUS.IDLE };
  }
  if (localMatch) {
    return { property: localMatch, status: DEEP_LINK_PROPERTY_STATUS.FOUND };
  }
  return remote;
};

export default useDeepLinkedProperty;
